import { ref, shallowRef } from 'vue';
import {
  ApiKeyMissingError,
  sendTurn,
  type ChatMessage,
  type PendingConfirmation,
} from './chat';
import { shipmentIdsFromToolResult } from './labels';

export type ToolStatus = 'running' | 'ok' | 'error' | 'declined';

export type TimelineItem =
  | { kind: 'user'; id: string; text: string }
  | { kind: 'assistant'; id: string; text: string; streaming: boolean }
  | {
      kind: 'tool';
      id: string;
      toolUseId: string;
      name: string;
      status: ToolStatus;
      text: string;
      shipmentIds: number[];
    }
  | {
      kind: 'confirm';
      id: string;
      toolUseId: string;
      name: string;
      input: unknown;
      state: 'pending' | 'approved' | 'declined';
    }
  | { kind: 'error'; id: string; text: string; retryable: boolean; requestId: string | null };

let sequence = 0;
const nextId = () => `item-${++sequence}`;

export function useChat() {
  /** Exactly what goes back to the model each turn. */
  const history = shallowRef<ChatMessage[]>([]);
  const timeline = ref<TimelineItem[]>([]);
  const busy = ref(false);
  const thinking = ref(false);
  const apiKeyMissing = ref(false);

  let currentAssistantId: string | null = null;
  let controller: AbortController | null = null;

  function push(item: TimelineItem): void {
    timeline.value = [...timeline.value, item];
  }

  function patch(id: string, update: Partial<TimelineItem>): void {
    timeline.value = timeline.value.map((item) =>
      item.id === id ? ({ ...item, ...update } as TimelineItem) : item,
    );
  }

  function appendToAssistant(text: string): void {
    if (currentAssistantId === null) {
      currentAssistantId = nextId();
      push({ kind: 'assistant', id: currentAssistantId, text, streaming: true });
      return;
    }

    const id = currentAssistantId;
    timeline.value = timeline.value.map((item) =>
      item.id === id && item.kind === 'assistant' ? { ...item, text: item.text + text } : item,
    );
  }

  function closeAssistantBubble(): void {
    if (currentAssistantId !== null) {
      patch(currentAssistantId, { streaming: false });
      currentAssistantId = null;
    }
  }

  async function runTurn(options: {
    approvals?: string[];
    declines?: Array<{ tool_use_id: string; reason?: string }>;
  }): Promise<void> {
    busy.value = true;
    thinking.value = false;
    controller = new AbortController();

    try {
      await sendTurn(
        {
          messages: history.value,
          approvals: options.approvals,
          declines: options.declines,
          signal: controller.signal,
        },
        {
          onTextDelta(text) {
            thinking.value = false;
            appendToAssistant(text);
          },
          onThinking() {
            thinking.value = true;
          },
          onToolStart(toolUseId, name) {
            closeAssistantBubble();
            push({
              kind: 'tool',
              id: nextId(),
              toolUseId,
              name,
              status: 'running',
              text: '',
              shipmentIds: [],
            });
          },
          onToolResult({ toolUseId, name, isError, declined, text }) {
            const existing = timeline.value.find(
              (item) => item.kind === 'tool' && item.toolUseId === toolUseId,
            );

            const status: ToolStatus = declined ? 'declined' : isError ? 'error' : 'ok';
            // Only offer a label for tools that actually produce shipments.
            const shipmentIds =
              !isError && (name === 'create_shipment' || name === 'get_shipment_label')
                ? shipmentIdsFromToolResult(text)
                : [];

            if (existing) {
              patch(existing.id, { status, text, shipmentIds });
            } else {
              push({
                kind: 'tool',
                id: nextId(),
                toolUseId,
                name,
                status,
                text,
                shipmentIds,
              });
            }
          },
          onConfirmationRequired(confirmation: PendingConfirmation) {
            closeAssistantBubble();
            push({
              kind: 'confirm',
              id: nextId(),
              toolUseId: confirmation.toolUseId,
              name: confirmation.name,
              input: confirmation.input,
              state: 'pending',
            });
          },
          onAppend(message) {
            history.value = [...history.value, message];
            if (message.role === 'assistant') {
              closeAssistantBubble();
            }
          },
          onError(report) {
            closeAssistantBubble();
            push({
              kind: 'error',
              id: nextId(),
              text: report.message,
              retryable: report.retryable,
              requestId: report.requestId,
            });
          },
          onDone() {
            closeAssistantBubble();
          },
        },
      );
    } catch (error) {
      closeAssistantBubble();
      if (error instanceof ApiKeyMissingError) {
        apiKeyMissing.value = true;
      } else if (!(error instanceof DOMException && error.name === 'AbortError')) {
        push({
          kind: 'error',
          id: nextId(),
          text: error instanceof Error ? error.message : String(error),
          // A transport failure never reached the model, so the same turn can be sent
          // again as-is.
          retryable: true,
          requestId: null,
        });
      }
    } finally {
      busy.value = false;
      thinking.value = false;
      controller = null;
    }
  }

  /**
   * `displayText` shows in the person's bubble while `text` goes to the model. Used by
   * the delivery-options picker, whose message carries a JSON block the model needs but
   * the person never typed. The full text still lands in `history`, so a retry re-sends
   * exactly what the model saw.
   */
  async function send(text: string, displayText?: string): Promise<void> {
    const trimmed = text.trim();
    if (!trimmed || busy.value) {
      return;
    }

    push({ kind: 'user', id: nextId(), text: displayText?.trim() || trimmed });
    history.value = [...history.value, { role: 'user', content: trimmed }];
    await runTurn({});
  }

  function findPendingConfirm(toolUseId: string): TimelineItem | undefined {
    return timeline.value.find(
      (item) => item.kind === 'confirm' && item.toolUseId === toolUseId && item.state === 'pending',
    );
  }

  async function approve(toolUseId: string): Promise<void> {
    const item = findPendingConfirm(toolUseId);
    if (!item || busy.value) {
      return;
    }
    patch(item.id, { state: 'approved' });
    await runTurn({ approvals: [toolUseId] });
  }

  async function decline(toolUseId: string, reason?: string): Promise<void> {
    const item = findPendingConfirm(toolUseId);
    if (!item || busy.value) {
      return;
    }
    patch(item.id, { state: 'declined' });
    await runTurn({ declines: [{ tool_use_id: toolUseId, reason }] });
  }

  function stop(): void {
    controller?.abort();
  }

  /**
   * Re-sends the turn that failed. Nothing is appended: `history` still ends where it
   * did, so the model receives exactly the conversation it received the first time.
   *
   * Only offered when history ends with a user message — a plain user turn or a batch of
   * tool results, both of which the API expects an assistant reply to. If a partial
   * assistant message got appended before the failure, repeating the request would ask
   * the model to continue mid-turn, which is a different thing than retrying.
   */
  function canRetry(): boolean {
    return !busy.value && history.value.at(-1)?.role === 'user';
  }

  async function retry(errorItemId: string): Promise<void> {
    if (!canRetry()) {
      return;
    }
    // Drop the error from the timeline so a second failure does not stack up.
    timeline.value = timeline.value.filter((item) => item.id !== errorItemId);
    await runTurn({});
  }

  /** Called when a key is stored elsewhere in the app, so the chat unblocks. */
  function clearApiKeyMissing(): void {
    apiKeyMissing.value = false;
  }

  function reset(): void {
    history.value = [];
    timeline.value = [];
    currentAssistantId = null;
    apiKeyMissing.value = false;
  }

  return {
    history,
    timeline,
    busy,
    thinking,
    apiKeyMissing,
    send,
    approve,
    decline,
    stop,
    retry,
    canRetry,
    reset,
    clearApiKeyMissing,
  };
}
